import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Info, Close } from '@mui/icons-material';
import { PALETTE, BORDERS, alpha } from '../theme';

const VARIANTS = {
    success: {
        icon: CheckCircle,
        bg:   alpha('primary', 0.08),
        text: PALETTE.primaryDark,
        accent: PALETTE.primary
    },
    error: {
        icon: ErrorIcon,
        bg:   'rgba(197, 82, 74, 0.08)',
        text: PALETTE.danger,
        accent: PALETTE.danger
    },
    info: {
        icon: Info,
        bg:   alpha('text', 0.04),
        text: PALETTE.text,
        accent: PALETTE.textMuted
    }
};

/**
 * Inline contextual status banner. Used in place of bottom-corner toasts.
 *
 * Renders nothing when `message` is empty/null. When `message` becomes truthy,
 * the banner slides open above the rendering position. It auto-dismisses
 * after `autoHideMs` (default 3500ms) unless the parent clears `message`
 * sooner — at which point the close animation runs.
 *
 * Two ways to use:
 *  1) Controlled — parent owns the message string + the clear callback:
 *       <InlineStatus message={msg} variant="success" onClose={() => setMsg('')} />
 *  2) Uncontrolled-ish — pass autoHideMs={0} to disable auto-dismiss.
 */
const InlineStatus = ({
    message,
    variant = 'info',
    autoHideMs = 3500,
    onClose,
    sx = {}
}) => {
    const [open, setOpen] = useState(Boolean(message));
    const timer = useRef(null);

    // Coordinate the "show on incoming message / hide after timeout" cycle.
    useEffect(() => {
        if (message) {
            setOpen(true);
            if (autoHideMs > 0) {
                clearTimeout(timer.current);
                timer.current = setTimeout(() => {
                    setOpen(false);
                    // Let the collapse animation finish before notifying the
                    // parent so it doesn't immediately unmount us mid-anim.
                    setTimeout(() => onClose && onClose(), 220);
                }, autoHideMs);
            }
        } else {
            setOpen(false);
        }
        return () => clearTimeout(timer.current);
    }, [message, autoHideMs, onClose]);

    if (!message && !open) return null;

    const v = VARIANTS[variant] || VARIANTS.info;
    const Icon = v.icon;

    return (
        <Collapse in={open} timeout={220}>
            <Box
                role={variant === 'error' ? 'alert' : 'status'}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    backgroundColor: v.bg,
                    border: BORDERS.soft,
                    borderLeft: `3px solid ${v.accent}`,
                    color: v.text,
                    ...sx
                }}
            >
                <Icon sx={{ fontSize: 20, color: v.accent, flexShrink: 0 }} />
                <Typography sx={{ flex: 1, fontSize: '0.95rem', fontWeight: 500, color: v.text }}>
                    {message}
                </Typography>
                {onClose && (
                    <IconButton
                        size="small"
                        aria-label="Dismiss status"
                        onClick={() => { setOpen(false); setTimeout(onClose, 220); }}
                        sx={{ color: v.text, opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>
        </Collapse>
    );
};

InlineStatus.propTypes = {
    message: PropTypes.string,
    variant: PropTypes.oneOf(['success', 'error', 'info']),
    autoHideMs: PropTypes.number,
    onClose: PropTypes.func,
    sx: PropTypes.object
};

export default InlineStatus;
